import { Button, Card, Modal, Table, Typography } from 'antd';
import React, { useState } from 'react';
import useSWR from 'swr';
import { z } from 'zod';

import { getAxiosWithAuth } from '@/lib/axios';
import { type Fix, fixSchema } from '@/types/database/fix';

import Detail from './Detail';
import Edit from './Edit';
import FixMessage from './FixMessage';
import { getColumns, getTitle } from './helpers';
import styles from './index.module.scss';
import type { FixTableData, ShowModalType } from './types';

const FixList = () => {
  const [showModal, setShowModal] = useState<ShowModalType>(null);
  const [selectedFix, setSelectedFix] = useState<FixTableData | null>(null);

  const { data: fixes, mutate: updateFixes } = useSWR<Fix[]>(
    'fix',
    async () => {
      const axios = getAxiosWithAuth();
      const { data } = await axios.get('/ezcon/api/fixes');
      const result = z.array(fixSchema).safeParse(data);
      if (!result.success) {
        console.error(result.error);
        return [];
      }
      return result.data;
    }
  );

  const columns = getColumns({
    onEdit: (fix) => {
      setShowModal('edit');
      setSelectedFix(fix);
    },
    onView: (fix) => {
      setShowModal('view');
      setSelectedFix(fix);
    },
    onMessage: (fix) => {
      setShowModal('message');
      setSelectedFix(fix);
    },
  });

  const dataSource: FixTableData[] = [];

  if (fixes) {
    fixes.forEach((fix) => {
      dataSource.push({
        ...fix,
        key: fix._id,
      });
    });
  }

  const title = getTitle(showModal, selectedFix);

  const closeModal = () => {
    setShowModal(null);
    setSelectedFix(null);
    updateFixes();
  };

  return (
    <div className={styles.fixList}>
      <div className={styles.buttons}>
        <Button onClick={() => setShowModal('create')}>新增</Button>
      </div>
      <Card>
        <Typography.Text type="secondary">* 點擊查看內容</Typography.Text>
        <Table
          bordered
          columns={columns}
          dataSource={dataSource}
          expandable={{
            expandedRowRender: (record) => (
              <p style={{ margin: 0 }}>{record.content}</p>
            ),
            showExpandColumn: false,
            expandRowByClick: true,
          }}
          scroll={{ x: 'max-content', y: 700 }}
        />
      </Card>
      <Modal
        open={showModal !== null}
        onCancel={closeModal}
        title={title}
        footer={null}
        centered
        bodyStyle={{
          maxHeight: 'calc(100vh - 200px)',
          overflowY: 'auto',
        }}
        width={700}
        destroyOnClose
      >
        {(showModal === 'create' || showModal === 'edit') && (
          <Edit
            key={Date.now()}
            onClose={closeModal}
            fixTableData={selectedFix}
            type={showModal}
          />
        )}
        {showModal === 'view' && selectedFix && (
          <Detail
            key={Date.now()}
            fixData={selectedFix}
            updateFixes={updateFixes}
          />
        )}
        {showModal === 'message' && selectedFix && (
          <FixMessage key={Date.now()} fix={selectedFix} />
        )}
      </Modal>
    </div>
  );
};

export default FixList;
